import { ForwardTableStatus } from "@/apis/forward/types";
import {
  AutoTagDisplayState,
  resolveDisplayState as resolveAutoTagDisplayState,
} from "@/components/mapping/auto-tag-progress/constants";

export type ForwardDisplayState =
  | "NOT_STARTED"
  | "PENDING"
  | "RUNNING"
  | "SUCCESS"
  | "EMPTY"
  | "FAILED"
  /** 스마트: 태깅할 값 셀이 없는 표(문장영역 등) — 아무것도 하지 않았다. */
  | "NOTHING_TO_TAG"
  /** 스마트: 체인 오토태깅 진행 중. */
  | "AUTO_TAGGING"
  /** 스마트: 체인 오토태깅이 새 태그 없이 끝남 — 태깅 미완일 수 있어 사람이 볼 표. */
  | "AUTO_EMPTY";

export const STATE_LABELS: Record<ForwardDisplayState, string> = {
  NOT_STARTED: "대기",
  PENDING: "대기 중",
  RUNNING: "불러오는 중",
  SUCCESS: "완료",
  EMPTY: "태그 없음",
  FAILED: "실패",
  NOTHING_TO_TAG: "건너뜀",
  AUTO_TAGGING: "오토태깅 중",
  AUTO_EMPTY: "확인",
};

/** Figma 5611:341515 — Chip/Text/Solid, 대기 상태 기준(Label/Disable + Label/Alternative). */
export const STATE_CHIP_CLASS: Record<ForwardDisplayState, string> = {
  NOT_STARTED: "bg-label-disable text-label-alternative",
  PENDING: "bg-label-disable text-label-alternative",
  RUNNING: "bg-blue-50 text-blue-600",
  SUCCESS: "bg-green-50 text-green-600",
  EMPTY: "bg-amber-50 text-amber-600",
  FAILED: "bg-red-50 text-red-600",
  NOTHING_TO_TAG: "bg-label-disable text-label-alternative",
  AUTO_TAGGING: "bg-blue-50 text-blue-600",
  // 확인은 amber(태그 없음)와 나란히 놓여도 구분되게 핑크로 둔다 — 이월 결과가
  // 비어 있는 것(정상 종료)과 오토태깅까지 하고도 못 채운 것(사람이 볼 표)은
  // 사용자 행동이 다르다.
  AUTO_EMPTY: "bg-pink-50 text-pink-600",
};

/**
 * 체인 오토태깅 단계 → forward 화면 상태.
 *
 * 판정 자체는 오토태깅 모달과 같은 리졸버를 쓰고 매핑만 얹는다 — 같은 실행 결과를
 * 두 화면이 다르게 보여주지 않게. Record 라서 오토태깅 상태가 늘면 컴파일이 깨진다
 * (새 상태가 조용히 "확인"으로 떨어지지 않는다).
 */
const AUTO_STATE_TO_FORWARD: Record<AutoTagDisplayState, ForwardDisplayState> =
  {
    PENDING: "AUTO_TAGGING",
    RUNNING: "AUTO_TAGGING",
    SUCCESS: "SUCCESS",
    // 축 대체는 오토태깅 모달과 동일하게 성공으로 접는다.
    AXIS_FALLBACK: "SUCCESS",
    // 새 태그 없이 끝났다 — 태깅이 덜 된 채 완료로 보이면 안 되므로 확인 대상.
    EMPTY: "AUTO_EMPTY",
    // 태그는 붙었지만 LINE_ITEM+MEMBER 를 다 갖춘 셀이 없다 — 역시 사람이 볼 표.
    NEEDS_REVIEW: "AUTO_EMPTY",
    ALREADY_TAGGED: "AUTO_EMPTY",
    SKIPPED: "AUTO_EMPTY",
    // 오토태깅 상태 행이 없는 표(NOT_STARTED). 그 행은 forward outcome 보다 먼저
    // 커밋되므로, AUTO_ENQUEUED 를 받은 시점에 없다는 건 첫 폴링 전 과도기가 아니라
    // 태스크가 유실된 상태다. 진행 중으로 두면 폴링이 영영 안 멈추므로 확인 대상으로
    // 떨어뜨린다 (isForwardRunning 도 같은 기준).
    NOT_STARTED: "AUTO_EMPTY",
    FAILED: "FAILED",
  };

type ForwardStatusInput = Pick<
  ForwardTableStatus,
  "status" | "outcome" | "autoStatus" | "autoOutcome"
>;

/**
 * 실패가 어느 단계에서 났는가. 사용자가 취할 조치가 다르다 — 이월 실패는 재실행,
 * 체인 오토태깅 실패는 이월까지는 됐으니 그 표만 직접 태깅하면 된다.
 */
export type ForwardFailureStage = "FORWARD" | "AUTO_TAG";

export const resolveFailureStage = (
  table: ForwardStatusInput | null,
): ForwardFailureStage | null => {
  if (resolveDisplayState(table) !== "FAILED") return null;
  return table?.outcome === "AUTO_ENQUEUED" ? "AUTO_TAG" : "FORWARD";
};

/**
 * BackgroundTask lifecycle(status) + 의미적 outcome → 화면 표시 상태.
 *
 * 상태 행 전체를 받는다 — 인자를 쪼개면 호출부가 auto* 를 빠뜨려도 컴파일이 통과하고,
 * 그 표는 화면에서 조용히 "오토태깅 중"에 고정된다. 실행 이력이 없으면 null.
 */
export const resolveDisplayState = (
  table: ForwardStatusInput | null,
): ForwardDisplayState => {
  if (table === null) return "NOT_STARTED";
  const { status, outcome } = table;
  if (status === null) return "NOT_STARTED";
  if (status === "PENDING") return "PENDING";
  if (status === "RUNNING") return "RUNNING";
  if (status === "FAILED") return "FAILED";
  if (outcome === "ERROR") return "FAILED";
  if (outcome === "EMPTY") return "EMPTY";
  // 이월만으로 태깅이 채워진 표 — 사용자에겐 일반 완료와 다르지 않다.
  if (outcome === "SKIPPED") return "SUCCESS";
  if (outcome === "NOTHING_TO_TAG") return "NOTHING_TO_TAG";
  if (outcome === "AUTO_ENQUEUED") {
    // 백엔드가 필드를 생략(undefined)해 내려도 null 과 같게 다룬다.
    const autoState = resolveAutoTagDisplayState(
      table.autoStatus ?? null,
      table.autoOutcome ?? null,
    );
    return AUTO_STATE_TO_FORWARD[autoState];
  }
  return "SUCCESS";
};
